/*
 * *****************************************************************************
 *  Copyright (C)  Motorola Solutions, INC.
 *  All Rights Reserved.
 *  Motorola Solutions Confidential Restricted.
 *  ******************************************************************************
 */

import {MessageType} from "CalltakingCoreApi";
import {sendCallMessageFail, sendDtmfMessage, sendRttMessage, sendSmsMessage, sendTddMessage} from "../../call/+state/call.actions";
import {environment} from "../../../environments/environment";

const MESSAGE_TYPE = {
    TDD: 'TDD',
    RTT: 'RTT',
    SMS: 'SMS',
    DTMF: 'DTMF'
} as const satisfies Record<string, MessageType>;

interface TranslationFields {
    clientMessageId?: string;
    sourceText?: string;
    sourceLanguage?: string;
    targetText?: string;
    targetLanguage?: string;
    messageLanguage?: string;
}

interface TranslationOutcome {
    msgToSend: string;
    translationFields: TranslationFields;
    placeholderMessageOverride: string | undefined;
    translationApplied: boolean;
    sentOriginal: boolean;
}

export function selectTranslationStrategy(
    message: string,
    messageId: string,
    translatedText: string | null,
    sourceLanguage: string | null,
    targetLanguage: string | null
): TranslationOutcome {
    if (isTranslationNotApplied(translatedText)) {
        return buildNoTranslationOutcome(message, messageId);
    }

    if (isSentOriginal(targetLanguage)) {
        return buildSentOriginalOutcome(message, messageId, translatedText!, sourceLanguage, targetLanguage!);
    }

    return buildTranslatedOutcome(message, messageId, translatedText!, sourceLanguage, targetLanguage);
}

function isTranslationNotApplied(translatedText: string | null): boolean {
    return translatedText === null;
}

function isSentOriginal(targetLanguage: string | null): boolean {
    return targetLanguage === environment.defaultLanguage;
}

export function buildSendMessageAction(messageType: MessageType, message: string, callId: string, clusterName: string,
    predefined: boolean, language: string, translation?: TranslationFields
) {
    const { clientMessageId, sourceText, sourceLanguage, targetText, targetLanguage, messageLanguage } = translation ?? {};
    switch (messageType) {
        case MESSAGE_TYPE.TDD:
            return sendTddMessage({ message, clusterName, callId, predefined, language,
                clientMessageId, sourceText, sourceLanguage, targetText, targetLanguage, messageLanguage });
        case MESSAGE_TYPE.RTT:
            return sendRttMessage({ message, clusterName, callId, predefined, language });
        case MESSAGE_TYPE.SMS:
            return sendSmsMessage({ message, clusterName, callId, predefined, language,
                clientMessageId, sourceText, sourceLanguage, targetText, targetLanguage, messageLanguage });
        case MESSAGE_TYPE.DTMF:
            return sendDtmfMessage({ message, clusterName, callId });
        default:
            return sendCallMessageFail({ payload: 'Unable to find or determine call message type.' });
    }
}

function buildNoTranslationOutcome(message: string, messageId: string): TranslationOutcome {
    return {
        msgToSend: message,
        translationFields: {
            clientMessageId: messageId,
            sourceText: undefined,
            sourceLanguage: undefined,
            targetText: undefined,
            targetLanguage: undefined,
            messageLanguage: undefined
        },
        placeholderMessageOverride: undefined,
        translationApplied: false,
        sentOriginal: false
    };
}

function buildSentOriginalOutcome(
    message: string,
    messageId: string,
    translatedText: string,
    sourceLanguage: string | null,
    targetLanguage: string
): TranslationOutcome {
    return {
        msgToSend: message,
        translationFields: {
            clientMessageId: messageId,
            sourceText: undefined,
            sourceLanguage: undefined,
            targetText: translatedText ?? undefined,
            targetLanguage,
            messageLanguage: sourceLanguage ?? undefined
        },
        placeholderMessageOverride: undefined,
        translationApplied: true,
        sentOriginal: true
    };
}

function buildTranslatedOutcome(
    message: string,
    messageId: string,
    translatedText: string,
    sourceLanguage: string | null,
    targetLanguage: string | null
): TranslationOutcome {
    const msgToSend = translatedText ?? message;

    return {
        msgToSend,
        translationFields: {
            clientMessageId: messageId,
            sourceText: message,
            sourceLanguage: sourceLanguage ?? undefined,
            targetText: undefined,
            targetLanguage: undefined,
            messageLanguage: targetLanguage ?? undefined
        },
        placeholderMessageOverride: msgToSend,
        translationApplied: true,
        sentOriginal: false
    };
}
